import { ThemeComponent } from "@/theme/ThemeComponent";
import { OptionSelector } from "@/utils/OptionSelectors";
import { Component, Prop, Ref, SimpleEvent, Watch } from "@eastsideco/salvo-ts";
import { ProductOption } from "./ProductOptionPicker";
import { formatMoney } from "@/utils/formatting";
import Swiper, { Navigation, Pagination, Scrollbar, Autoplay, Thumbs, Controller, SwiperOptions } from 'swiper';
import 'swiper/css/bundle';

interface ValueRadio extends HTMLInputElement {
    dataset: {
        value: string;
    };
}
interface VariantAdditional {
    id: number;
    short: string | null;
    imgsrc: string;
}
@Component
export class ProductButtonOption extends ThemeComponent implements ProductOption {
    @Prop name!: string;
    @Prop values!: string[];
    @Prop selected!: string | null;
    @Prop position!: number;
    @Prop({ default: null }) variants!: VariantAdditional[];
    @Ref buttons!: ValueRadio[];
    @Prop({ default: false }) noSelect!: boolean;
    @Ref({ required: false }) noSelectOption!: HTMLLabelElement;

    public nonSelected = this.noSelect || false;
    private valuesWithoutStock: string[] = [];
    private valuesWithoutVariant: string[] = [];
    private _onChange = new SimpleEvent<string | null>();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    targetVariants: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    selectedVariant: any;

    public get onChange() {
        return this._onChange.asEvent();
    }

    private open: string = '';
    private _onOpenChange = new SimpleEvent<string>();

    public get onOpenChange() {
        return this._onOpenChange.asEvent();
    }

    public changeOpen(open: string) {
        this.open = open;

        // Use the private backing field to dispatch
        this._onOpenChange.dispatch(open);
    }
    private close: string = '';
    private _onCloseChange = new SimpleEvent<string>();

    public get onCloseChange() {
        return this._onCloseChange.asEvent();
    }

    public changeClose(close: string) {
        this.close = close;

        // Use the private backing field to dispatch
        this._onCloseChange.dispatch(close);
    }
    @Watch('targetVariants')
    protected handeVariantsChange() {
        this.updateButtonStyles();
    }

    init() {
        this.updateGalleryCount();
        if (this.noSelectOption) {
            this.nonSelected = true;
            this.$listen(this.noSelectOption, 'click', () => this.nonSelected = true);
            // Add keyboard support for noSelectOption
            this.$listen(this.noSelectOption, 'keydown', (e: KeyboardEvent) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    this.noSelectOption.click();
                }
            });
        }
        for (const button of this.buttons) {
            this.$listen(button, 'change', () => {
                const value = button.value || null;
                this.select(value);
                this._onChange.dispatch(value);
                this.updateSlider();
                this.updatethumbnail();
                this.updateGalleryCount();
                this.updateAddons();
            });
            if (this.noSelectOption) {
                this.$listen(button, 'click', () => {
                    if (this.nonSelected) {
                        this.nonSelected = false;
                    }
                });
            }
            // Add keyboard support for variant labels
            const label = button.parentNode as HTMLLabelElement;
            if (label && label.classList.contains('product-form__button-item')) {
                this.$listen(label, 'keydown', (e: KeyboardEvent) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        button.click();
                    }
                });
            }
        }
        this.updateButtonStyles();
        this._onChange.dispatch(this.selected);
        document.addEventListener('openOptionDrawer', () => {
            this._onOpenChange.dispatch(this.open);
        });
    }

    @Watch('nonSelected')
    protected handlenonselect() {
        if (this.nonSelected) {
            if (this.noSelectOption.parentNode) {
                const siblings = this.noSelectOption.parentNode.children;
                for (const sibling of siblings) {
                    sibling.classList.remove('is-active');
                }
                this.noSelectOption.classList.add('is-active');
            }
        } else {
            this.noSelectOption.classList.remove('is-active');
        }
        this.updateButtonStyles();
        document.dispatchEvent(new Event('buildProdFormSummary'));
    }
    public select(value: string | null) {
        this.selected = value;
        this.updateButtonStyles();
        this._onCloseChange.dispatch(this.close);
    }

    public updateBasedOnSelector(selector: OptionSelector) {
        this.valuesWithoutStock = selector.options[this.position - 1].values
            .filter((v) => v.outOfStock).map((v) => v.value);
        this.valuesWithoutVariant = selector.options[this.position - 1].values
            .filter((v) => !v.variantExists).map((v) => v.value);
        this.targetVariants = selector.options[this.position - 1].values
            .filter((v) => v.targetVariant).map((v) => v.targetVariant);
    }

    private updateButtonStyles() {
        for (const [index, button] of this.buttons.entries()) {
            const selectHold = (button.value || null) == this.selected;
            if (selectHold && this.targetVariants) {
                this.selectedVariant = this.targetVariants[index];
            }
        }
        for (const [index, button] of this.buttons.entries()) {
            const value = button.value;
            const selected = (value || null) == this.selected;
            const outOfStock = this.valuesWithoutStock.indexOf(value) > -1;
            const noVariant = this.valuesWithoutVariant.indexOf(value) > -1;
            if (button.parentNode == null) { return; }
            const $wrapper = button.parentNode as HTMLElement;
            if (!this.nonSelected) {
                selected ? $wrapper.classList.add('is-active') : $wrapper.classList.remove('is-active');
            }
            $wrapper.classList.remove('no-variant', 'out-of-stock', 'available');
            $wrapper.classList.add(noVariant ? 'no-variant' : (outOfStock ? 'out-of-stock' : 'available'));
            const price = $wrapper.querySelector('.product-form__button-price');
            const diff = $wrapper.querySelector('.product-form__button-price-difference');
            const state = $wrapper.querySelector('.product-form__button-state');
            const remove = $wrapper.querySelector('.product-form__button-remove') as HTMLBodyElement;
            const description = $wrapper.querySelector('.product-form__button-description');
            const img = $wrapper.querySelector('.product-form__button-item-image');
            if (!this.targetVariants) { return; }
            const target = this.targetVariants[index];
            if (!target) { return; }
            if (diff) {
                if (selected && !this.nonSelected) {
                    diff.textContent = 'Selected';
                } else if (this.targetVariants) {
                    diff.textContent = '';
                }
            }

            if (state) {
                if (selected && !this.nonSelected) {
                    state.textContent = 'Selected';
                } else if (this.targetVariants) {
                    state.textContent = '';
                }
            }

            if (remove) {
                if (selected && !this.nonSelected) {
                    remove.textContent = 'Remove';
                } else if (this.targetVariants) {
                    remove.textContent = '+' + formatMoney(Math.abs(target.price)) as string;
                }
                remove.onclick = (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const targetSelector = '.product-form__button-item--addons[data-ref="noSelectOption"]';
                    const selector = remove.closest('.product-form__options');
                    const noneOption = selector?.querySelector<HTMLElement>(targetSelector);
                    if (noneOption) {
                        noneOption.click();
                    }
                };
            }

            if (img) {
                img.innerHTML = '';
            }
            if (price) {
                price.textContent = formatMoney(Math.abs(target.price)) as string;
            }
            if (!this.variants) { return; }
            const chosen = this.variants.find((i: VariantAdditional) => i.id == target.id);
            if (!chosen) { return; }
            if (description) {
                description.textContent = chosen.short;
            }
            if (img) {
                if (chosen.imgsrc) {
                    img.innerHTML = `<img src="${chosen.imgsrc}" alt="${target.title}">`;
                    img.classList.remove('product-form__button-item-image--empty');
                } else {
                    img.classList.add('product-form__button-item-image--empty');
                }
            }
        }
    }

    private updateSlider() {
        const slider = document.querySelector('.product-gallery__main') as HTMLElement & { swiper?: Swiper };
        if (slider && slider.swiper) {
            // Move the slider to the first slide
            slider.swiper.slideTo(0);
            // Remove the hidden class from the first slide when an option is changed
            if (this.selected) {
                const slides = document.querySelectorAll('[data-ref="first"]');
                for (const slide of slides) {
                    slide.classList.remove('hidden');
                    slide.classList.add('product-gallery__media-item');
                }
            }

            slider.swiper.update();
        }
    }

    private updatethumbnail() {
        const thumbSlider = document.querySelector('.product-gallery__thumbnails') as HTMLElement & { swiper?: Swiper };
        if (thumbSlider?.swiper) {
            // Check if thumbnail first hidden slide exists
            const thumbFirstSlide = thumbSlider.querySelector('[data-ref="first"]');

            if (thumbFirstSlide) {
                thumbFirstSlide.remove();
            }

            // Clone the first slide from the main slider and add to the thumbnail swiper
            const mainFirstSlide = document.querySelector('.product-gallery__main [data-ref="first"]');
            thumbSlider.swiper.slideTo(0); // Optionally, move thumbnail slider to the first slide

            if (mainFirstSlide) {
                const clone = mainFirstSlide.cloneNode(false) as HTMLElement; // false = shallow clone
                const img = mainFirstSlide.querySelector('img');
                if (img) {
                    const imgClone = img.cloneNode(true);
                    clone.appendChild(imgClone);
                }

                clone.classList.remove('hidden');
                clone.classList.add('product-gallery__media-item');

                // Insert into the thumbnail swiper
                const thumbsWrapper = thumbSlider.querySelector('.swiper-wrapper');
                if (thumbsWrapper) {
                    thumbsWrapper.insertBefore(clone, thumbsWrapper.firstChild);
                }
            }

            // Update thumbnail swiper after DOM changes
            thumbSlider.swiper.update();
        }
    }

    // to add the overlay effect on the thubnail gallery
    private updateGalleryCount() {
        const thumbSlider = document.querySelector('.product-gallery__thumbnails') as HTMLElement & { swiper?: Swiper };
        if(thumbSlider) {
            const slides = thumbSlider.querySelectorAll('.product-gallery__media');
            const sliderCount = slides.length;

            if (sliderCount > 9) {
                thumbSlider.classList.add('overflow-gallery');
            } else {
                thumbSlider.classList.remove('overflow-gallery');
            }

            const swiperInstance = thumbSlider.swiper;
            if (!swiperInstance) {return;}

            const updateGalleryClass = () => {
                if (swiperInstance.isEnd) {
                    thumbSlider.classList.remove('overflow-gallery');
                }else{
                    thumbSlider.classList.add('overflow-gallery');
                }
            };

            swiperInstance.on('slideChange', updateGalleryClass);
        }
    }

    private updateAddons() {
        const addons = document.querySelector('.product-addons');
        const addonGroups = addons?.querySelectorAll('.product-addons__variant-group');
        if(!addonGroups) {return;}
        for (const addonGroup of addonGroups) {
            const id = addonGroup.getAttribute('id');
            if(id == this.selectedVariant.id) {
                addonGroup.classList.add('is-active');
            } else {
                addonGroup.classList.remove('is-active');
            }
        }
    }

}

